import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Upload, X, GripVertical, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';

/**
 * Multi-image gallery uploader for admin products.
 * Features:
 *  - native picker (mobile gallery + desktop file explorer)
 *  - drag & drop on desktop
 *  - client-side compression (max 1600px, JPEG 0.85 quality)
 *  - preview before save, delete, reorder (drag handle)
 *  - returns array of /api/uploads/img_*.jpg URLs via onChange(values)
 */
export const ImageGalleryUpload = ({ value = [], onChange, required = true, label = 'Product Images' }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const inputRef = useRef(null);

  const compress = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1600;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) return reject(new Error('Compression failed'));
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.85);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const uploadFiles = useCallback(async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (list.length === 0) {
      toast.error('No image files selected');
      return;
    }
    setUploading(true);
    setProgress({ current: 0, total: list.length });
    const uploaded = [];
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      if (f.size > 20 * 1024 * 1024) {
        toast.error(`${f.name}: file too large (max 10MB before compression)`);
        continue;
      }
      try {
        const compressed = await compress(f);
        const formData = new FormData();
        formData.append('file', compressed);
        const { data } = await axios.post(`${API}/uploads/image`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push(data.url);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload failed: ${f.name}`);
      }
      setProgress({ current: i + 1, total: list.length });
    }
    if (uploaded.length) {
      onChange([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} image(s) uploaded`);
    }
    setUploading(false);
    setProgress({ current: 0, total: 0 });
  }, [onChange, value]);

  const handleFile = (e) => {
    if (e.target.files?.length) uploadFiles(e.target.files);
    e.target.value = ''; // allow re-select same file
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  };

  const handleReorderStart = (idx) => setDragIdx(idx);
  const handleReorderEnter = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...value];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    onChange(next);
    setDragIdx(idx);
  };
  const handleReorderEnd = () => setDragIdx(null);

  return (
    <div className="space-y-3" data-testid="image-gallery-upload">
      {label && <label className="block text-sm" style={{ color: '#A3B8AC' }}>{label} {required && <span style={{ color: '#EF4444' }}>*</span>}</label>}

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className="rounded-xl p-6 text-center cursor-pointer transition-all"
        style={{
          background: dragOver ? 'rgba(212,175,55,0.18)' : 'rgba(212,175,55,0.06)',
          border: `2px dashed ${dragOver ? 'rgba(212,175,55,0.8)' : 'rgba(212,175,55,0.4)'}`,
        }}
        data-testid="dropzone"
      >
        <ImagePlus className="w-10 h-10 mx-auto mb-2" style={{ color: '#D4AF37' }} />
        <p className="font-medium mb-1" style={{ color: '#F8FAF9' }}>
          {uploading ? `Uploading ${progress.current}/${progress.total}...` : 'Drop images here, or click to browse'}
        </p>
        <p className="text-xs" style={{ color: '#A3B8AC' }}>Max 20MB each · auto-compressed to ≤1600px · PNG/JPG/WebP</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFile}
          disabled={uploading}
          className="hidden"
          data-testid="gallery-file-input"
        />
      </div>

      {/* Gallery */}
      {value.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3" data-testid="gallery-preview">
          {value.map((url, idx) => (
            <div
              key={`${url}-${idx}`}
              draggable
              onDragStart={() => handleReorderStart(idx)}
              onDragEnter={() => handleReorderEnter(idx)}
              onDragEnd={handleReorderEnd}
              onDragOver={(e) => e.preventDefault()}
              className="relative rounded-lg overflow-hidden group"
              style={{ background: 'rgba(0,0,0,0.3)', border: idx === 0 ? '2px solid #D4AF37' : '1px solid rgba(255,255,255,0.1)' }}
              data-testid={`gallery-item-${idx}`}
            >
              <img
                src={url}
                alt={`#${idx + 1}`}
                className="w-full aspect-square object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="%23202824" width="200" height="200"/><text x="100" y="100" fill="%23EF4444" font-family="sans-serif" font-size="14" text-anchor="middle" dy=".3em">Failed</text></svg>';
                }}
              />
              <div className="absolute inset-0 flex items-start justify-between p-1.5">
                <span className="px-1.5 py-0.5 rounded text-xs font-mono" style={{ background: 'rgba(0,0,0,0.7)', color: idx === 0 ? '#D4AF37' : '#F8FAF9' }}>
                  {idx === 0 ? 'MAIN' : `#${idx + 1}`}
                </span>
                <button type="button" onClick={() => removeAt(idx)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded" style={{ background: 'rgba(239,68,68,0.9)', color: 'white' }} data-testid={`gallery-remove-${idx}`}>
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="absolute bottom-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-move p-1 rounded" style={{ background: 'rgba(0,0,0,0.7)', color: '#A3B8AC' }}>
                <GripVertical className="w-3 h-3" />
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs" style={{ color: '#A3B8AC' }}>
        First image is the main thumbnail. Drag to reorder.
      </p>
    </div>
  );
};
